import { useCallback, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import { getLiveDonates } from '../../api/payment/live-donate.api';
import { LiveDonate, mergeLiveDonates } from '../../utils/live-donate';

/**
 * Tải lượt donate lớn gần nhất rồi nghe lượt mới qua nhóm "live-donate". Lỗi thì im lặng.
 *
 * `enabled` (mặc định true): khi false thì không gọi API và không mở kết nối SignalR. Dùng để
 * tránh mở 2 kết nối cùng lúc khi cả khung cột phải (máy tính) và dải điện thoại đều được gắn
 * vào cây component song song, chỉ khác nhau ở việc ẩn/hiện bằng class CSS responsive.
 *
 * Chỉ giữ kết nối khi tab đang hiện: chuyển sang tab khác (`document.visibilityState === 'hidden'`)
 * thì đóng kết nối; quay lại tab thì tải lại danh sách và mở kết nối mới — đỡ tốn kết nối
 * SignalR mở ngầm khi người dùng không nhìn vào trang.
 */
export const useLiveDonates = (limit: number, enabled = true) => {
  const [items, setItems] = useState<LiveDonate[]>([]);
  const [freshId, setFreshId] = useState<string | null>(null);
  const freshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  const load = useCallback(() => {
    if (!enabled) return;
    getLiveDonates(20)
      .then((list) => setItems((prev) => mergeLiveDonates(prev, list || [], limit)))
      .catch(() => undefined);
  }, [limit, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const stopConnection = () => {
      const connection = connectionRef.current;
      connectionRef.current = null;
      connection?.stop();
    };

    const startConnection = () => {
      if (connectionRef.current) return;
      load();
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?channel=live-donate`, {
          transport: signalR.HttpTransportType.WebSockets,
          skipNegotiation: true,
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Warning)
        .build();
      connection.on('ReceiveLiveDonate', (d: LiveDonate) => {
        setItems((prev) => mergeLiveDonates(prev, [d], limit));
        setFreshId(d.id);
        if (freshTimer.current) clearTimeout(freshTimer.current);
        freshTimer.current = setTimeout(() => setFreshId(null), 4000);
      });
      connection.onreconnected(() => load());
      connectionRef.current = connection;
      connection.start().catch(() => undefined);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stopConnection();
      } else {
        startConnection();
      }
    };

    if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
      startConnection();
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      if (freshTimer.current) clearTimeout(freshTimer.current);
      stopConnection();
    };
  }, [limit, load, enabled]);

  return { items, freshId };
};
